<template>
    <page-header></page-header>
      <div class="product-table-heading">
        <div>
          <h1>{{ $labels.product }}s</h1>
        </div>
        <div>
          <button v-if="ENABLE_ASSESSMENT" class="addtasks-button" @click="viewAssessmentTasks">Assessment Tasks</button>
          <button class="addchapter-button" @click="viewChapters">Chapters</button>
          <button class="addchapter-button" @click="viewAircraft">Aircraft</button>
          <button class="addchapter-button" @click="viewSpecifics">Specifics</button>
          <button class="addproduct-button" @click="openAddProduct()">Add {{ $labels.product }}</button>
        </div>
      </div>

      <div class="container-fluid-project">
          <vue-good-table v-if="products.length > 0" :columns="columns" :rows="processedRows" :pagination-options="{
            enabled: true,
            perPage: perPage
          }" :search-options="{ enabled: false }" :sort-options="{
            enabled: true,
          }">
            <!-- Tasks Button -->
            <template #table-row="props">
              <!-- Tasks column -->
              <span v-if="props.column.field === 'tasks'">
                <button class="view-button" @click="viewProduct(props.row)">
                  View Tasks
                </button>
              </span>

              <!-- Created At column -->
              <span v-else-if="props.column.field === 'created_at'">
                {{ formatDate(props.row.created_at) }}
              </span>

              <!-- Actions column -->
              <span v-else-if="props.column.field === 'actions'">
                <font-awesome-icon icon="eye" class="eye-icon" :title="`View details of ${this.$labels.product}`"
                  @click="viewProductDetails(props.row.product_id)" />
                <font-awesome-icon 
                  icon="edit" 
                  class="edit-icon"
                  :title="`Edit ${this.$labels.product}`"
                  @click="openEditProduct(props.row.product_id)" 
                />
                <font-awesome-icon icon="trash-alt" class="delete-icon" :title="`Delete ${this.$labels.product}`"
                  @click="deleteProduct(props.row.product_id)" />
              </span>

              <!-- Default rendering -->
              <span v-else :title="String(props.formattedRow[props.column.field] ?? '')">
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table>

          <div v-else class="no-products">
            <p style="color: red; margin-top: 50px;">There are no {{ $labels.product }}s. Click on <strong>Add {{ $labels.product }}</strong> to add {{ $labels.product }}s.</p>
          </div>
        </div>
    <product-modal v-if="showProductModal" :mode="modalMode" :product="selectedProduct" :products="products"
      @close="closeProductModal" @saved="fetchProducts" />
    <product-details-modal v-if="showProductDetails" :tasks="tasks" @close="closeProductDetails"></product-details-modal>
</template>

<script>
import axios from 'axios';
import PageHeader from '@/components/Examiner/Header';
import { VueGoodTable } from 'vue-good-table-next';
import 'vue-good-table-next/dist/vue-good-table-next.css';
import ProductModal from './AddEditProductModal.vue';
import ProductDetailsModal from './ProductDetailsModal.vue';
import { ENABLE_ASSESSMENT as ENABLE_ASSESSMENT_FLAG, ENABLE_PASS_RATE } from '@/config/featureFlags';

export default {
  name: 'viewProducts',
  components: {
    PageHeader, // Register the PageHeader component
    VueGoodTable,
    ProductModal,
    ProductDetailsModal,
  },
  data() {
    return {
      username: '',
      products: [],
      tasks: [],
      perPage: 10,
      showProductModal: false,
      modalMode: "add",   // or 'edit'
      selectedProduct: null,
      showProductDetails: false,
    };
  },
  async mounted() {
    // Check the user's role before accessing this page
    const role = localStorage.getItem('role');
    if (role !== 'practical_manager' && role !== 'examiner' && role !== 'admin') {
      // Redirect to the login page if the user is not an examiner
      this.$router.push({ name: 'LoginPage' });
    }
    this.fetchProducts();
    this.fetchTasks();

  },
  computed: {
    ENABLE_ASSESSMENT() {
      return ENABLE_ASSESSMENT_FLAG;
    },
    columns() {
      const baseColumns = [
        {
          label: 'ID',
          field: 'product_id',
          sortable: true,
          thClass: 'vgt-id-column',
          tdClass: 'vgt-id-column',
        },
        {
          label: 'Name',
          field: 'name',
          sortable: true,
          thClass: 'vgt-name-column',
          tdClass: 'vgt-name-column',
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterFn: (rowValue, filterValue) => {
              if (!filterValue) return true;
              return String(rowValue || '')
                .toLowerCase()
                .includes(String(filterValue).toLowerCase());
            }
          }
        },
        ...(this.ENABLE_ASSESSMENT ? [{
          label: 'Assessments',
          field: 'assessment_count',
          sortable: true,
          thClass: 'vgt-category-column',
          tdClass: 'vgt-category-column',
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterFn: (rowValue, filterValue) => {
              if (!filterValue) return true;
              return String(rowValue || '').includes(String(filterValue));
            }
          }
        }] : []),
        {
          label: 'Tasks',
          field: 'taskCount',
          sortable: true,
          thClass: 'vgt-id-column',
          tdClass: 'vgt-id-column',
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterFn: (rowValue, filterValue) => {
              if (!filterValue) return true;
              return String(rowValue || '').includes(String(filterValue));
            }
          }
        },
        {
          label: 'View Tasks',
          field: 'tasks',
          sortable: false,
          thClass: 'vgt-actions-column',
          tdClass: 'vgt-actions-column'
        },
        ...(ENABLE_PASS_RATE ? [{
          label: 'Pass Rate (%)',
          field: 'pass_rate',
          sortable: true,
          thClass: 'vgt-id-column',
          tdClass: 'vgt-id-column',
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterDropdownItems: [...new Set(this.products.map(p => p.pass_rate).filter(r => r != null))].sort((a, b) => a - b).map(r => ({ value: r, text: `${r}` })),
            filterFn: (rowValue, filterValue) => {
              if (!filterValue) return true;
              return String(rowValue) === String(filterValue);
            }
          }
        }] : []),
        // {
        //   label: 'Created At',
        //   field: 'created_at',
        //   sortable: true,
        //   thClass: 'vgt-category-column',
        //   tdClass: 'vgt-category-column',
        //   filterOptions: {
        //     enabled: true,
        //     placeholder: 'All',
        //     filterFn: (rowValue, filterValue) => {
        //       if (!filterValue) return true;

        //       const rawValue = String(rowValue || '').toLowerCase();
        //       const formattedValue = this.formatDate(rowValue).toLowerCase();
        //       const filter = String(filterValue).toLowerCase();

        //       return rawValue.includes(filter) || formattedValue.includes(filter);
        //     }
        //   }
        // },
      ];

      const actionColumn = [
        {
          label: 'Actions',
          field: 'actions',
          sortable: false,
          thClass: 'vgt-actions-column',
          tdClass: 'vgt-actions-column'
        },
      ];

      return [...baseColumns, ...actionColumn];
    },
    processedRows() {
      const taskCountMap = this.tasks.reduce((acc, task) => {
        acc[task.product_id] = (acc[task.product_id] || 0) + 1;
        return acc;
      }, {});

      return this.products.map(product => ({
        ...product,
        taskCount: taskCountMap[product.product_id] || 0
      }));
    },
  },
  methods: {
    async fetchProducts() {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        let result = await axios.get('/api/products', { headers });
        this.products = result.data;
        this.username = localStorage.getItem('username');
      }
      catch (error) {
        console.error('Error fetching Products:', error);
      }
    },
    async fetchTasks() {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        let result = await axios.get('/api/tasks', { headers });
        this.tasks = result.data;
      }
      catch (error) {
        console.error('Error fetching Products:', error);
      }
    },
    openAddProduct() {
      this.modalMode = "add";
      this.selectedProduct = null;
      this.showProductModal = true;
    },
    openEditProduct(product_id) {
      this.modalMode = "edit";
      this.selectedProduct = this.products.find(s => s.product_id === product_id);
      this.showProductModal = true;
    },
    closeProductModal() {
      this.showProductModal = false;
      this.fetchProducts();
    },
    viewChapters() {
      this.$router.push({ name: 'ViewChapters' });
    },
    viewAssessmentTasks() {
      this.$router.push({ name: 'ViewAssessmentTasks' });
    },
    viewAircraft() {
      this.$router.push({ name: 'ViewAircraft' });
    },
    viewSpecifics() {
      this.$router.push({ name: 'ViewSpecifics' });
    },
    closeProductsModal() {
      this.showProductsModal = false;
      this.fetchProducts();
    },
    viewProductDetails(product_id) {
      localStorage.setItem('product_id', product_id);
      this.showProductDetails = true; // Show the modal when the button is clicked
    },
    closeProductDetails() {
      this.showProductDetails = false; // Close the modal
    },
    viewProduct(product) {
      const productId = parseInt(product.product_id, 10);
      this.$store.commit('SET_SELECTED_PRODUCT', product);
      localStorage.setItem('product_id', productId);
      this.$router.push({
        name: 'productPage', params: { productId: product.product_id }
      });
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
      const day = String(date.getDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    },
    async deleteProduct(id) {
      if (!confirm(`Are you sure you want to delete this ${this.$labels.product}?`)) return;
      if (!confirm(`This action will PERMANENTLY delete the ${this.$labels.product} from the database. Do you wish to continue?`)) return;

      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const response = await axios.delete(`/api/products/${id}`, { headers });

        if (response.status === 200) {
          // await removeItemFromCacheByUrl(`/api/products/${id}`);
          this.products = this.products.filter(a => String(a.product_id) !== String(id));
          window.alert(`${this.$labels.product} deleted successfully`);
        }
      } catch (error) {
        if (error.isOffline === true && error.queued) {
          this.products = this.products.filter(a => String(a.product_id) !== String(id));
          alert('You are offline. The delete has been queued and will sync later.');
          return;
        }

        if (error.response?.status === 409) {
          alert(`This ${this.$labels.product} cannot be deleted because it contains one or more ${this.$labels.project}s.`);
        } else {
          alert('An error occurred while deleting.');
        }
      }
    },
  },
};
</script>

<style scoped>
h1 {
  padding-top: 0px;
}

.product-table-heading {
  margin-top: 20px;
  margin-bottom: 10px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 3%;
  padding-right: 20px;
}

.container-fluid-project {
  padding-top: 10px;
  width: 97%;
  margin-left: 1.5%;
  margin-right: 1.5%;
  display: flex;
  justify-content: center;
}

.view-button {
  border-radius: 4px;
  background-color: #ffffff;
  color: #004fa3;
  font-size: 13px;
  padding: 5px;
  width: 100px;
  border: none;
  font-family: Verdana, Geneva, Tahoma, sans-serif;
  transition: transform 0.1s ease-in-out;
}

.view-button:hover {
  background-color: #ffffff;
  cursor: pointer;
  transform: scale(1.01);
}

.addproduct-button {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  background-color: #0a407a;
  color: #ffffff;
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  min-width: 220px;
  margin-left: 15px;
  margin-bottom: 15px;
  cursor: pointer;
  margin-bottom: auto;
  font-family: Verdana, Geneva, Tahoma, sans-serif;
  transition: transform 0.2s ease-in-out;
}

.addproduct-button:hover {
  background-color: #00356e;
  cursor: pointer;
  transform: scale(1.01);
  border: 1px solid #062444;
}

.addchapter-button {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  background-color: #5a5f64;
  color: #ffffff;
  margin-right: 15px;
  font-size: 14px;
  border: none;
  border-radius: 4px;
  min-width: 130px;
  height: 35px;
  font-family: Verdana, Geneva, Tahoma, sans-serif;
  transition: transform 0.2s ease-in-out;
}

.addchapter-button:hover {
  background-color: #4a4e52;
  cursor: pointer;
  transform: scale(1.01);
  border: 1px solid #062444;
}

.addtasks-button {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  background-color: #275b8f;
  color: #ffffff;
  margin-right: 15px;
  font-size: 14px;
  border: none;
  border-radius: 4px;
  min-width: 180px;
  height: 35px;
  font-family: Verdana, Geneva, Tahoma, sans-serif;
  transition: transform 0.2s ease-in-out;
}

.addtasks-button:hover {
  background-color: #174d83;
  cursor: pointer;
  transform: scale(1.01);
  border: 1px solid #042242;
}

.edit-icon,
.eye-icon {
  font-size: 16px;
  /* Adjust the size as needed */
  color: #615f5f;
  /* Change the color to your preference */
  cursor: pointer;
  transition: color 0.2s, transform 0.2s, box-shadow 0.2s;
  margin-right: 10px;
  margin-left: 10px;
}

.edit-icon:hover,
.eye-icon:hover {
  color: #2b2a2a;
  /* Change the color to your preference */
  cursor: pointer;
  transform: scale(1.1);
  /* Example transform for scaling on hover */
}

.icon-disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.icon-disabled:hover {
  opacity: 0.3;
  cursor: not-allowed;
}

.delete-icon {
  font-size: 16px;
  color: #FF0000;
  /* Red color */
  cursor: pointer;
  transition: color 0.2s, transform 0.2s, box-shadow 0.2s;
  margin-left: 10px;
  margin-right: 10px;
}

.delete-icon:hover {
  color: #a82222;
  cursor: pointer;
  transform: scale(1.1);
}
</style>